import { Capacitor } from "@capacitor/core";

import type {
  JsonPayload,
  JsonRepositoryHelperContract,
  JsonTransactionContract,
  JsonTransactionManagerContract,
} from "@core/ports/json-storage";
import type { ZodObject, ZodType } from "@core/zod";

import {
  IndexedDbClient,
  IndexedDbRepositoryHelper,
  IndexedDbTransaction,
} from "@react/foundation/browser";

import { DeviceRepositoryHelper } from "./DeviceRepositoryHelper.ts";
import { SqliteClient } from "./SqliteClient.ts";
import { SqliteRepositoryHelper } from "./SqliteRepositoryHelper.ts";
import { SqliteTransaction } from "./SqliteTransaction.ts";

type Client =
  | { readonly client: IndexedDbClient; readonly type: "indexedDb" }
  | { readonly client: SqliteClient; readonly type: "sqlite" };

export class DeviceStorageManager
  implements JsonTransactionManagerContract<JsonTransactionContract>
{
  public readonly __typeTag = "JsonTransactionManagerContract";

  private readonly client: Client;

  public constructor({
    database,
    tables,
    version,
  }: {
    readonly database: string;
    readonly tables: readonly string[];
    readonly version: number;
  }) {
    this.client = Capacitor.isNativePlatform()
      ? {
          client: new SqliteClient({ database, tables, version }),
          type: "sqlite",
        }
      : {
          client: new IndexedDbClient({ database, tables, version }),
          type: "indexedDb",
        };
  }

  public async connect(): Promise<void> {
    await this.client.client.connect();
  }

  public createRepositoryHelper<
    Identifier extends ZodObject,
    Data extends ZodObject,
    EntityType extends string,
  >({
    entityType,
    readSchema,
    tableName,
  }: {
    readonly entityType: EntityType;
    readonly readSchema: ZodType<JsonPayload<Identifier, Data>>;
    readonly tableName: string;
  }): JsonRepositoryHelperContract<
    Identifier,
    Data,
    JsonTransactionContract,
    EntityType
  > {
    switch (this.client.type) {
      case "indexedDb":
        return new DeviceRepositoryHelper({
          helper: new IndexedDbRepositoryHelper<Identifier, Data, EntityType>({
            entityType,
            indexedDb: this.client.client,
            readSchema,
            tableName,
          }),
          transactionClass: IndexedDbTransaction,
        });
      case "sqlite":
        return new DeviceRepositoryHelper({
          helper: new SqliteRepositoryHelper<Identifier, Data, EntityType>({
            entityType,
            readSchema,
            sqlite: this.client.client,
            tableName,
          }),
          transactionClass: SqliteTransaction,
        });
    }
  }

  public startTransaction(): JsonTransactionContract {
    switch (this.client.type) {
      case "indexedDb":
        return new IndexedDbTransaction({ indexedDb: this.client.client });
      case "sqlite":
        return new SqliteTransaction({ sqlite: this.client.client });
    }
  }
}
